import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../lib/constants';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Edit, Trash2, Plus, Search, ChevronLeft, ChevronRight, Download, FileText, Phone, Mail, User, Stethoscope, Activity } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useNotification } from '../contexts/NotificationContext';

const ResponsivePatientTable = () => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const rowsPerPage = 5;

  // Move fetchData to component scope so it can be called elsewhere
  const fetchData = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/list_patients`, { withCredentials: true });
      console.log('Data fetched successfully:', response.data);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      showNotification('error', 'Failed to fetch patient records');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleExportPDF = async () => {
    const doc = new jsPDF();

    // Add logo (replace with your actual image)
    const logoUrl = '/logo.png'; // Path to your logo (public folder)

    // Load image as Base64
    const toDataURL = (url) => new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = url;
    });

    try {
      const logoData = await toDataURL(logoUrl);
      doc.addImage(logoData, 'PNG', 14, 10, 30, 30); // (image, format, x, y, width, height)
    } catch (error) {
      console.error('Failed to load logo image', error);
    }

    // Title below the logo
    doc.setFontSize(18);
    doc.text('Patient List', 50, 25); // (text, x, y)

    const tableColumn = ["Patient Name", "Mobile No", "UHID No", "Email"];
    const tableRows = [];

    filteredPatients.forEach((row) => {
      const rowData = [
        row.name,
        row.mobile,
        row.uhid_no,
        row.email,
      ];
      tableRows.push(rowData);
    });

    autoTable(doc, {
      startY: 45, // push below logo + title
      head: [tableColumn],
      body: tableRows,
      headStyles: {
        fillColor: [0, 128, 128], // Teal
        textColor: [255, 255, 255],
      },
      alternateRowStyles: {
        fillColor: [240, 240, 240],
      },
    });

    doc.save('patient_list.pdf');
  };


  // Updated search functionality - only searches by name, mobile, and email
  const filteredPatients = data.filter(patient =>
    (patient.name || '').toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
    (patient.mobile || '').toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
    (patient.email || '').toString().toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Paginated patients
  const totalPages = Math.ceil(filteredPatients.length / rowsPerPage);
  const paginatedPatients = filteredPatients.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  const handleEdit = (uhid_no) => {
    navigate(`/editPatient/${uhid_no}`, {
      state: { from: '/patientTable' }
    });
  };

  const handleDelete = (patient) => {
    setDeleteConfirm(patient);
  };

  const confirmDelete = async () => {
    const uhid_no = deleteConfirm?.uhid_no;
    if (!uhid_no) return;

    if (!window.confirm(`Are you sure you want to delete the patient with UHID: ${uhid_no}?`)) {
      return;
    }

    try {
      const response = await axios.delete(`${API_BASE_URL}/delete_patient/${uhid_no}`, { withCredentials: true });
      if (response.status === 200) {
        setData(prev => prev.filter(item => item.uhid_no !== uhid_no));
        await fetchData();
        setDeleteConfirm(null);
        showNotification('success', "Patient deleted successfully.");
      } else {
        showNotification('error', "Failed to delete patient.");
      }
    } catch (error) {
      console.error("Error deleting patient:", error);
      showNotification('error', "An error occurred while deleting the patient.");
    }
  };

  const handleSurgeryForm = (uhid_no) => {
    window.location.href = `/mainPage/${uhid_no}`;
  };

  const handleAddPatient = () => {
    console.log('Navigate to add patient page');
  };

  const resetSearch = () => {
    setSearchTerm('');
    setCurrentPage(0);
  };

  if (loading) {
    return (
      <div className="w-full max-w-6xl mx-auto p-4">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          <div className="h-12 bg-gray-200 rounded mb-6"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 space-y-6">

      {/* Action bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="relative w-full lg:w-2/3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search patients by name, mobile number, or email..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(0);
              }}
              className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all"
            />
            {searchTerm && (
              <button
                onClick={resetSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>

          <div className="flex gap-3 w-full lg:w-auto">
            <button
              onClick={() => window.location.href = '/addPatient'}
              className="flex items-center gap-2 bg-teal-600 text-white px-4 py-3 rounded-lg hover:bg-teal-700 transition-colors font-medium flex-1 lg:flex-none justify-center"
            >
              <Plus className="h-4 w-4" />
              Add Patient
            </button>

            <button
              onClick={handleExportPDF}
              className="flex items-center gap-2 bg-gray-600 text-white px-4 py-3 rounded-lg hover:bg-gray-700 transition-colors font-medium flex-1 lg:flex-none justify-center"
            >
              <Download className="h-4 w-4" />
              Export PDF
            </button>
          </div>
        </div>
      </div>

      {/* Results summary */}
      {searchTerm && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-800">
            Found {filteredPatients.length} patient{filteredPatients.length !== 1 ? 's' : ''} matching "{searchTerm}"
          </p>
        </div>
      )}

      {/* Desktop Table */}
      <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-teal-50 border-b border-teal-100">
                <th className="text-left p-4 font-semibold text-teal-900">S/No</th>
                <th className="text-left p-4 font-semibold text-teal-900">Patient Name</th>
                <th className="text-left p-4 font-semibold text-teal-900">Mobile No</th>
                <th className="text-left p-4 font-semibold text-teal-900">UHID No</th>
                <th className="text-left p-4 font-semibold text-teal-900">Email</th>
                <th className="text-center p-4 font-semibold text-teal-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedPatients.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-12 text-center">
                    <div className="flex flex-col items-center">
                      <Search className="h-16 w-16 text-gray-300 mb-4" />
                      <h3 className="text-lg font-medium text-gray-500 mb-2">No patients found</h3>
                      <p className="text-gray-400 mb-4">Try searching by patient name, mobile number, or email address</p>
                      {searchTerm && (
                        <button
                          onClick={resetSearch}
                          className="text-teal-600 hover:text-teal-700 font-medium"
                        >
                          Clear search
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedPatients.map((patient, index) => (
                  <tr
                    key={patient.uhid_no}
                    className={`border-b border-gray-100 hover:bg-teal-50/50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                      }`}
                  >
                    <td className="p-4">
                      {currentPage * rowsPerPage + index + 1}
                    </td>
                    <td className="p-4">
                      <div className="font-medium text-gray-900">{patient.name}</div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone className="h-4 w-4" />
                        {patient.mobile}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded font-mono text-sm">
                        {patient.uhid_no}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail className="h-4 w-4" />
                        <span className="truncate max-w-xs">{patient.email}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex justify-center gap-1">
                        <button
                          onClick={() => handleEdit(patient.uhid_no)}
                          className="flex items-center gap-1 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors"
                          title="Edit Patient Information"
                        >
                          <Edit className="h-4 w-4" />
                          <span className="hidden sm:inline">Edit</span>
                        </button>
                        <button
                          onClick={() => handleSurgeryForm(patient.uhid_no)}
                          className="flex items-center gap-1 px-3 py-2 text-sm text-green-600 hover:bg-green-50 border border-green-200 rounded-lg transition-colors"
                          title="Create Surgery Form"
                        >
                          <Stethoscope className="h-4 w-4" />
                          <span className="hidden sm:inline">Surgery</span>
                        </button>
                        <button
                          onClick={() => handleDelete(patient)}
                          className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors"
                          title="Delete Patient"
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="hidden sm:inline">Delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {paginatedPatients.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <Search className="h-16 w-16 text-gray-300 mb-4 mx-auto" />
            <h3 className="text-lg font-medium text-gray-500 mb-2">No patients found</h3>
            <p className="text-gray-400 mb-4">Try searching by patient name, mobile number, or email address</p>
            {searchTerm && (
              <button
                onClick={resetSearch}
                className="text-teal-600 hover:text-teal-700 font-medium"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          paginatedPatients.map((patient) => (
            <div key={patient.uhid_no} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 text-lg mb-1">{patient.name}</h3>
                  <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded font-mono text-sm">
                    {patient.uhid_no}
                  </span>
                </div>
              </div>

              <div className="space-y-3 mb-4">
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">{patient.mobile}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600 truncate">{patient.email}</span>
                </div>
              </div>

              {/* Action buttons for mobile */}
              <div className="flex gap-2 pt-3 border-t border-gray-100">
                <button
                  onClick={() => handleEdit(patient.uhid_no)}
                  className="flex-1 flex items-center justify-center gap-2 py-2 px-3 text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors text-sm font-medium"
                  title="Edit Patient Information"
                >
                  <Edit className="h-4 w-4" />
                  Edit
                </button>
                <button
                  onClick={() => handleSurgeryForm(patient.uhid_no)}
                  className="flex-1 flex items-center justify-center gap-2 py-2 px-3 text-green-600 hover:bg-green-50 border border-green-200 rounded-lg transition-colors text-sm font-medium"
                  title="Create Surgery Form"
                >
                  <Stethoscope className="h-4 w-4" />
                  Surgery
                </button>
                <button
                  onClick={() => handleDelete(patient)}
                  className="flex-1 flex items-center justify-center gap-2 py-2 px-3 text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors text-sm font-medium"
                  title="Delete Patient"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {filteredPatients.length > 0 && totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-600">
              Showing {currentPage * rowsPerPage + 1} to {Math.min((currentPage + 1) * rowsPerPage, filteredPatients.length)} of {filteredPatients.length} patients
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(0)}
                disabled={currentPage === 0}
                className="px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 0}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-lg">
                {currentPage + 1} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= totalPages - 1}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages - 1)}
                disabled={currentPage >= totalPages - 1}
                className="px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Last
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Patient</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <strong>{deleteConfirm.name}</strong> (UHID: {deleteConfirm.uhid_no})?
              This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Patient
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResponsivePatientTable;